// Import the functions you need from the SDKs you need
import { initializeApp } from "firebase/app";
import { getAuth, connectAuthEmulator } from "firebase/auth";
import {
  connectFirestoreEmulator,
  initializeFirestore,
  persistentLocalCache,
  persistentMultipleTabManager,
} from "firebase/firestore";
import { connectFunctionsEmulator, getFunctions } from "firebase/functions";
import { initializeAppCheck } from "firebase/app-check";
import { createAppCheckProvider } from './appCheckProvider';
import { runtimeConfig } from "../config/runtime";

const firebaseConfig = {
  apiKey: runtimeConfig.firebase.apiKey,
  authDomain: runtimeConfig.firebase.authDomain,
  projectId: runtimeConfig.firebase.projectId,
  storageBucket: runtimeConfig.firebase.storageBucket,
  messagingSenderId: runtimeConfig.firebase.messagingSenderId,
  appId: runtimeConfig.firebase.appId,
  measurementId: runtimeConfig.firebase.measurementId,
};

const missingFirebaseConfig = ['apiKey', 'authDomain', 'projectId', 'appId']
  .filter((key) => !firebaseConfig[key]);
if (missingFirebaseConfig.length) {
  throw new Error(`Missing Firebase configuration: ${missingFirebaseConfig.join(', ')}`);
}

// Initialize Firebase
const app = initializeApp(firebaseConfig);
const auth = getAuth(app);
const db = initializeFirestore(app, {
  localCache: persistentLocalCache({ tabManager: persistentMultipleTabManager() }),
});
const functions = getFunctions(app);

const appCheckSiteKey = runtimeConfig.firebase.appCheckSiteKey;
if (appCheckSiteKey && typeof window !== 'undefined') {
  if (runtimeConfig.firebase.appCheckDebugToken && !runtimeConfig.isProduction) {
    window.FIREBASE_APPCHECK_DEBUG_TOKEN = runtimeConfig.firebase.appCheckDebugToken;
  }
  initializeAppCheck(app, {
    provider: createAppCheckProvider(appCheckSiteKey, runtimeConfig.firebase.appCheckProvider),
    isTokenAutoRefreshEnabled: true,
  });
}

// Route Firebase calls to local emulators whenever we are running locally.
const hostname =
  typeof window !== "undefined" && typeof window.location !== "undefined"
    ? window.location.hostname
    : "";

const isLocalNetworkHost = (value) => {
  if (!value) {
    return false;
  }
  if (["localhost", "127.0.0.1", "::1", "0.0.0.0"].includes(value)) {
    return true;
  }
  if (value.endsWith(".local")) {
    return true;
  }
  if (/^192\.168\./.test(value)) {
    return true;
  }
  if (/^10\./.test(value)) {
    return true;
  }
  if (/^172\.(1[6-9]|2\d|3[0-1])\./.test(value)) {
    return true;
  }
  return false;
};

const isLocalhost = isLocalNetworkHost(hostname);
const isDevBuild = !runtimeConfig.isProduction;

const emulatorOptIn = runtimeConfig.firebase.emulatorMode;
const emulatorMode = emulatorOptIn || "auto";
const allowEmulators = isDevBuild || isLocalhost;
const shouldUseEmulators = allowEmulators && emulatorMode === "true";
const shouldAutoDetect = allowEmulators && emulatorMode === "auto";

const formatHttpAddress = (host, port) => {
  if (!host) {
    return `http://127.0.0.1:${port}`;
  }
  if (host.includes(":") && !host.startsWith("[")) {
    return `http://[${host}]:${port}`;
  }
  return `http://${host}:${port}`;
};

const resolvedHost = runtimeConfig.firebase.firestoreHost || hostname || "127.0.0.1";
const authHost =
  runtimeConfig.firebase.authEmulatorHost ||
  formatHttpAddress(resolvedHost, 9099);
const firestorePort = runtimeConfig.firebase.firestorePort;
const firestoreProbeUrl = `${formatHttpAddress(resolvedHost, firestorePort)}/emulator/v1/projects/${firebaseConfig.projectId}/databases/(default)/documents`;

const detectFirestoreEmulator = async (timeoutMs = 350) => {
  if (typeof fetch === "undefined") {
    return false;
  }

  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeoutMs);

  try {
    await fetch(firestoreProbeUrl, {
      method: "GET",
      mode: "no-cors",
      signal: controller.signal,
    });
    return true;
  } catch (err) {
    return false;
  } finally {
    clearTimeout(timeoutId);
  }
};

let firebaseConnectionMode = 'production';
let firebaseInitializationError = null;

const firebaseReady = (async () => {
  if (shouldUseEmulators) {
    connectAuthEmulator(auth, authHost, { disableWarnings: true });
    connectFirestoreEmulator(db, resolvedHost, firestorePort);
    connectFunctionsEmulator(functions, resolvedHost, runtimeConfig.firebase.functionsPort);
    firebaseConnectionMode = 'emulator';
    return true;
  }

  if (!shouldAutoDetect) {
    return false;
  }

  const emulatorAvailable = await detectFirestoreEmulator();
  if (emulatorAvailable) {
    connectAuthEmulator(auth, authHost, { disableWarnings: true });
    connectFirestoreEmulator(db, resolvedHost, firestorePort);
    connectFunctionsEmulator(functions, resolvedHost, runtimeConfig.firebase.functionsPort);
    firebaseConnectionMode = 'emulator';
    return true;
  }

  return false;
})().catch((error) => {
  firebaseInitializationError = error;
  throw error;
});

const getFirebaseDiagnostics = () => ({
  mode: firebaseConnectionMode,
  emulatorEnabled: firebaseConnectionMode === 'emulator',
  projectId: firebaseConfig.projectId,
  emulatorUiUrl: runtimeConfig.firebase.emulatorUiUrl,
  error: firebaseInitializationError,
});

export { auth, db, functions, firebaseReady, getFirebaseDiagnostics };
